import React, { useEffect, useMemo, useState } from 'react';
import {
  Delete, Return, Exclude, Watch, BlockAuthor, ToPodiumTitle
} from '../../../composed-components/Actions';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { SEND_TO_CRM } from '../../../core/constants';
import ResizeTable from '../../../composed-components/Resize/Resize';
import { BASEFONTSIZE, COLUMNSHEADING, PAGESIZE, REFETCHDELAY, RefetchOptions } from '../constants';
import { EFontSize, TColumns } from '../types/types';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import Pagination from '../../../components/Pagination';
import getAllColumns from '../columns/getAllColumns';
import { useUserPermit } from '../../../context/User.context';
import { SENT_TO_CRM_RR, SOURCE } from '../../../core/constants/endpoints';
import { useActions } from '../../../context/Actions.context';
import getDataSource from '../utils/getDataSource';
import styled from 'styled-components';

const TableFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SendToCRM = () => {
  const { source: sourcePath, isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { edit_titles } = useUserPermit();

  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);
  const [deletedItems, setDeletedItems] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('All Titles');

  const { filter } = useFilter();
  const { sort } = useSort();

  const deleteBooks = book => setDeletedItems(book);
  const changeFontSize = size => setFontSize(size);
  const changePage = p => setPage(p);

  const isBookstat = sourcePath['source'].includes(SOURCE.bookstat);
  const TAB_ID = isBookstat ? SEND_TO_CRM : SENT_TO_CRM_RR;

  const { data = [], isLoading, isFetching, refetch } = useBooksList(
    TAB_ID, {
      page,
      ...filter[TAB_ID],
      ...sort[TAB_ID]
    });

  const { count, refetch: refetchRowCount } = useDataRowCount(TAB_ID, { ...filter[TAB_ID] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch();
      refetchRowCount();
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortColumns = (title: string) => (
    <Sorting id={TAB_ID} title={title} changePage={changePage} />
  );

  const thead = useMemo(() => {
    const allColumns = getAllColumns(sortColumns);
    const head = COLUMNSHEADING[TAB_ID].map(title => allColumns[title]);

    if (edit_titles) {
      head.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className={'actions'}>
            <Delete book={book} id={TAB_ID} />
            <BlockAuthor book={book} id={TAB_ID} />
            <Exclude book={book} id={TAB_ID} />
            <ToPodiumTitle book={book} id={TAB_ID} />
            <Watch book={book} id={TAB_ID} />
            <Return book={book} id={TAB_ID} />
          </div>
        )
      });
    }

    return head;
  }, [edit_titles, COLUMNSHEADING, sortColumns]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource = getDataSource(data, selectedOption, isBookstat);

  useEffect(() => {
    if (!isRefreshed) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }

    return () => {
      setSelectedOption('All Titles');
    };
  }, [page, filter[TAB_ID], sort[TAB_ID]]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm
          id={TAB_ID}
          page={page}
          changePage={changePage}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
        <Settings
          thead={thead}
          id={TAB_ID}
          deleteBooks={deleteBooks}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteItems={deletedItems}
        />
      </div>
      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            id={TAB_ID}
            dataSource={dataSource}
            deleteItems={deletedItems}
            isFetching={isFetching || isActionLoading}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter>
            <Pagination
              total={count}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default React.memo(SendToCRM);